/**
 * The J+P brand tokens, in one place.
 *
 * Ported verbatim from `tailwind.config.ts`'s `jp` palette and `app/globals.css` in the Next.js
 * app. **The values are the contract, not the names**: these are the colours the existing
 * product uses, and the rewrite is a change of component library (W4), not a redesign.
 *
 * They live in their own module rather than inline in the MUI theme because Tailwind is
 * retained alongside MUI for layout and the brand system — §4 of the standards explicitly
 * permits that — so the same values have to reach both. One source, two consumers.
 */

export const jp = {
  /** Near-black. The page ground behind the video, and body text. */
  ink: "#000000",
  /**
   * The accent. Named `navy` for historical reasons and it is blue — every interactive
   * element, focus ring and glow uses it, so changing it changes the app's whole character.
   */
  navy: "#3369FF",
  navyDark: "#1F4FD8",
  /** Muted body copy and field labels. */
  slate: "#57534E",
  /** The warm off-white the cards sit on. */
  mist: "#FAF0E1",
  /** Hairline borders. */
  line: "#E5DED0",
  /**
   * A lighter tint of the accent. Named `goldLight` when the palette was ported, which was
   * doubly misleading — it is blue, and there was a `gold` beside it holding the accent's
   * exact value under a second name. `gold` is gone; this one earns its place as MUI's
   * `primary.light` and the selection highlight, so it keeps the value and loses the name.
   */
  accentLight: "#7FA1FF",

  /*
   * The semantic trio, in two shades each. Which one to reach for is decided by contrast,
   * not by taste:
   *
   *  - the base is a *fill, border and icon* colour. Bright enough to read as a signal at a
   *    glance, but against this app's near-white card it sits at 2.28:1 (success) to 3.67:1
   *    (danger) — below the 4.5:1 WCAG AA requires for body text, and far below it for the
   *    11px uppercase a Chip label uses.
   *  - the `*Text` shade is the same hue taken dark enough to clear 4.5:1 on a card (7:1+ on
   *    pure white), and is what every *word* rendered in a semantic colour must use.
   *
   * Keeping both means an alert stays visually loud — bright border, bright icon — while its
   * text stays legible, instead of trading one for the other. The base values are unchanged
   * from the Next.js app, so nothing about the app's colour *character* moves here.
   */
  success: "#22C55E",
  successText: "#166534",
  warning: "#D97706",
  warningText: "#92400E",
  danger: "#F43F5E",
  dangerText: "#9F1239",
} as const;

/**
 * The corner radii, as three explicit pixel values rather than one number and a lot of
 * multiplication.
 *
 * **The multiplication was the bug.** MUI's `sx` treats `borderRadius: 3` as *three times*
 * `theme.shape.borderRadius`, and this theme set that base to 24 (Tailwind's `xl2`, the card
 * radius the Next.js app used). So every `borderRadius: 3` on an inner panel was rendering at
 * **72px** and every `borderRadius: 2` at **48px** — on a 56px thumbnail, a radius larger than
 * the box, i.e. a circle. Those call sites were written as if the base were MUI's default 4px,
 * which would have given the 8-12px they plainly intended.
 *
 * The fix is to stop multiplying: these are absolute, and call sites spell out which one they
 * want. `shape.borderRadius` below is set to `RADIUS_INNER_PX` so that any multiplier that
 * creeps back in lands somewhere sane rather than at 72px.
 *
 * The scale is deliberately tight — 6px surfaces, 4px controls, 2px thumbnails — and buttons
 * and chips are not pills. Radius is applied to outer surfaces and controls only; panels
 * nested inside a card stay square.
 */
export const RADIUS_PX = 6;

/**
 * Controls: buttons, chips, inputs, selectable cards, the drop zone, alerts. Panels nested
 * inside a card are deliberately square — a rounded box inside a rounded box is what made
 * the page read as soft all over.
 */
export const RADIUS_INNER_PX = 4;

/** Thumbnails and small previews, where a large radius eats the image. */
export const RADIUS_SMALL_PX = 2;

/**
 * The frosted-glass card.
 *
 * Ported as `rgba(255,255,255,0.8)`, raised to 0.9 here. At 0.8 the darkened video showed
 * through enough to drag everything on a card down a contrast band — `slate` body copy to
 * ~4.7:1 and the navy labels to ~2.8:1, i.e. failing AA on a surface that carries most of
 * the app's text. The frosting is the *backdrop blur*, not the tint, so 0.9 is visually the
 * same material while putting every text colour used on a card comfortably past 4.5:1 over
 * any video frame.
 */
export const CARD_SURFACE = {
  background: "rgba(255, 255, 255, 0.9)",
  backdropFilter: "blur(16px)",
  border: `1px solid ${jp.line}99`,
} as const;

export const SHADOWS = {
  card: "0 1px 2px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.05)",
  panel: "0 8px 30px rgba(0,0,0,0.12)",
} as const;

/**
 * The one moment in this app that is meant to feel like a showpiece: a generated image.
 *
 * Only the shadow and ring change on hover — no movement — which is why this is deliberately
 * **not** gated on `prefers-reduced-motion`. That preference is about motion, not about a
 * static glow's intensity, and the video background (which is motion) honours it separately.
 */
export const GLOW_FRAME = {
  borderRadius: `${RADIUS_PX}px`,
  boxShadow: "0 8px 30px -12px rgba(51, 105, 255, 0.25)",
  outline: "1px solid rgba(51, 105, 255, 0.15)",
  transition: "box-shadow 300ms, outline-color 300ms",
  "&:hover": {
    boxShadow: "0 12px 40px -10px rgba(51, 105, 255, 0.4)",
    outlineColor: "rgba(51, 105, 255, 0.3)",
  },
} as const;

/**
 * Text sitting directly on the video needs its own shadow to stay legible over a moving
 * image — the frame behind any given word is not knowable in advance.
 */
export const TEXT_ON_VIDEO = {
  textShadow: "0 1px 2px rgba(0, 0, 0, 0.7), 0 1px 8px rgba(0, 0, 0, 0.5)",
} as const;

/**
 * Figtree, as the Next.js app loaded it through `next/font`. Vite has no equivalent, so the
 * face is loaded by the stylesheet and this is only the stack — with a real fallback, because
 * a webfont that fails to load must not leave the app in a serif default.
 */
export const FONT_STACK = '"Figtree", system-ui, -apple-system, "Segoe UI", sans-serif';
